import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('username'); // username, password, totp, biometric, pending, setup-totp, setup-bio
  const [userInfo, setUserInfo] = useState(null);
  const [totpSetup, setTotpSetup] = useState(null);
  const [pendingLogin, setPendingLogin] = useState(null); // {token, user} saved during setup steps

  const goHome = (user) => navigate(user.role === 'customer' ? '/portal' : '/dashboard', { replace: true });

  // Complete login with stored pending data
  const completeLogin = () => {
    if (pendingLogin) {
      loginWithToken(pendingLogin.token, pendingLogin.user);
      goHome(pendingLogin.user);
    }
  };

  // Step 1: Check what auth methods the user has
  const checkUser = async () => {
    if (!username.trim()) return;
    setError('');
    setBusy(true);
    try {
      const res = await api.post('/auth/check-user', { username });
      setUserInfo(res.data);
      if (res.data.approvalStatus === 'pending') {
        setStep('pending');
      } else if (res.data.hasBiometric) {
        setStep('biometric');
      } else if (res.data.hasTotp && res.data.role === 'admin') {
        setStep('totp');
      } else {
        setStep('password');
      }
    } catch {
      setStep('password'); // Fallback to password if check fails
    } finally {
      setBusy(false);
    }
  };

  // Password login (then check if needs TOTP/biometric setup)
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (userInfo?.hasTotp) {
        // Admin with TOTP: password + code
        const res = await api.post('/auth/totp/login', { username, password, code: totpCode });
        if (!userInfo?.hasBiometric) {
          localStorage.setItem('token', res.data.token);
          setPendingLogin({ token: res.data.token, user: res.data.user });
          setStep('setup-bio');
        } else {
          loginWithToken(res.data.token, res.data.user);
          goHome(res.data.user);
        }
      } else {
        // Do a "silent" login — get token but don't save to state yet
        const res = await api.post('/auth/login', { username, password });
        const token = res.data.token;
        const user = res.data.user;
        localStorage.setItem('token', token);
        setPendingLogin({ token, user });

        // Check if needs to set up TOTP or biometric
        if (user.role === 'admin' && !userInfo?.hasTotp) {
          setStep('setup-totp');
        } else if (!userInfo?.hasBiometric) {
          setStep('setup-bio');
        } else {
          // All set — complete login
          loginWithToken(token, user);
          goHome(user);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || t('auth.wrongCredentials'));
    } finally {
      setBusy(false);
    }
  };

  // Biometric login
  const handleBiometricLogin = async () => {
    setError('');
    setBusy(true);
    try {
      const optionsRes = await api.post('/auth/webauthn/login-options', { username });
      const { userId, ...options } = optionsRes.data;
      const credential = await startAuthentication({ optionsJSON: options });
      const verifyRes = await api.post('/auth/webauthn/login-verify', { userId, credential });
      loginWithToken(verifyRes.data.token, verifyRes.data.user);
      goHome(verifyRes.data.user);
    } catch (err) {
      if (err.response?.data?.error === 'pending_approval') {
        setStep('pending');
      } else {
        setError(err.response?.data?.error || 'Biometric login failed. Try password.');
        setStep('password');
      }
    } finally {
      setBusy(false);
    }
  };

  // Setup TOTP (admin)
  const handleSetupTotp = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await api.post('/auth/totp/setup');
      setTotpSetup(res.data);
    } catch (err) {
      setError('Failed to setup TOTP');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyTotp = async () => {
    setError('');
    setBusy(true);
    try {
      await api.post('/auth/totp/verify', { code: totpCode });
      alert('TOTP enabled! Now set up biometric for faster login.');
      // Save pending login for the next step
      const token = localStorage.getItem('token');
      if (token && !pendingLogin) {
        try { const me = await api.get('/auth/me'); setPendingLogin({ token, user: me.data.user }); } catch {}
      }
      setStep('setup-bio');
    } catch {
      setError('Invalid code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // Setup biometric
  const handleSetupBiometric = async () => {
    setError('');
    setBusy(true);
    try {
      const optionsRes = await api.post('/auth/webauthn/register-options');
      const credential = await startRegistration({ optionsJSON: optionsRes.data });
      const verifyRes = await api.post('/auth/webauthn/register-verify', { credential, deviceName: navigator.userAgent.slice(0, 50) });
      if (verifyRes.data.needsApproval) {
        setStep('pending');
      } else {
        alert('Biometric registered!');
        completeLogin();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Biometric setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>{t('app.title')}</h1>
        <div className="subtitle">{t('app.subtitle')}</div>
        {error && <div className="error">{error}</div>}

        {/* Step: Username */}
        {step === 'username' && (
          <form onSubmit={e => { e.preventDefault(); checkUser(); }}>
            <div className="form-group">
              <label>{t('auth.username')}</label>
              <input className="input" value={username} onChange={e => setUsername(e.target.value)} autoFocus
                maxLength={64} pattern="[a-zA-Z0-9._-]+" autoComplete="username" required />
            </div>
            <button className="btn btn-gold" type="submit" disabled={busy}>
              {busy ? t('common.loading') : 'Next'}
            </button>
          </form>
        )}

        {/* Step: Biometric login */}
        {step === 'biometric' && (
          <div>
            <p style={{ textAlign: 'center', marginBottom: 16, color: 'var(--text-dim)' }}>
              👋 {username}
            </p>
            <button className="btn btn-gold" style={{ width: '100%', marginBottom: 12, fontSize: 16, padding: '14px 0' }}
              onClick={handleBiometricLogin} disabled={busy}>
              {busy ? t('common.loading') : '🔐 Login with Biometric'}
            </button>
            <button className="btn btn-outline" style={{ width: '100%' }}
              onClick={() => setStep(userInfo?.hasTotp ? 'totp' : 'password')}>
              Use {userInfo?.hasTotp ? 'TOTP code' : 'password'} instead
            </button>
          </div>
        )}

        {/* Step: Password (+ optional TOTP) */}
        {(step === 'password' || step === 'totp') && (
          <form onSubmit={handlePasswordLogin}>
            <p style={{ textAlign: 'center', marginBottom: 12, color: 'var(--text-dim)' }}>
              👋 {username}
            </p>
            <div className="form-group">
              <label>{t('auth.password')}</label>
              <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus />
            </div>
            {(step === 'totp' || userInfo?.hasTotp) && (
              <div className="form-group">
                <label>Authenticator Code</label>
                <input className="input" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="6-digit code" maxLength={6} inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code"
                  style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20 }} required />
              </div>
            )}
            <button className="btn btn-gold" type="submit" disabled={busy}>
              {busy ? t('common.loading') : t('auth.login')}
            </button>
            <button type="button" className="btn btn-outline" style={{ width: '100%', marginTop: 8 }}
              onClick={() => { setStep('username'); setPassword(''); setTotpCode(''); setError(''); }}>
              ← Back
            </button>
          </form>
        )}

        {/* Step: Pending approval */}
        {step === 'pending' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <h3>Waiting for Admin Approval</h3>
            <p style={{ color: 'var(--text-dim)', marginTop: 8 }}>
              Your device registration is pending. The admin will approve your access.
            </p>
            <button className="btn btn-outline" style={{ marginTop: 16 }}
              onClick={() => { setStep('username'); setError(''); }}>
              ← Back
            </button>
          </div>
        )}

        {/* Step: Setup TOTP (admin first time) */}
        {step === 'setup-totp' && (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: 12 }}>Setup Google Authenticator</h3>
            {!totpSetup ? (
              <button className="btn btn-gold" onClick={handleSetupTotp} disabled={busy}>
                {busy ? t('common.loading') : 'Generate QR Code'}
              </button>
            ) : (
              <div>
                <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 12 }}>
                  Scan this QR code with Google Authenticator:
                </p>
                <img src={totpSetup.qrCode} alt="TOTP QR" style={{ width: 200, height: 200, margin: '0 auto', display: 'block' }} />
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, wordBreak: 'break-all' }}>
                  Manual key: {totpSetup.secret}
                </p>
                <div className="form-group" style={{ marginTop: 16 }}>
                  <label>Enter 6-digit code to verify:</label>
                  <input className="input" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" maxLength={6} inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code"
                    style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20 }} />
                </div>
                <button className="btn btn-gold" onClick={handleVerifyTotp} disabled={busy || totpCode.length !== 6}>
                  Verify & Enable
                </button>
              </div>
            )}
            <button className="btn btn-outline" style={{ marginTop: 12, width: '100%' }}
              onClick={completeLogin}>
              Skip for now
            </button>
          </div>
        )}

        {/* Step: Setup biometric */}
        {step === 'setup-bio' && (
          <div style={{ textAlign: 'center' }}>
            <h3 style={{ marginBottom: 12 }}>Setup Biometric Login</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, marginBottom: 16 }}>
              Register Face ID / Touch ID / Fingerprint for faster login.
            </p>
            <button className="btn btn-gold" style={{ width: '100%', fontSize: 16, padding: '14px 0' }}
              onClick={handleSetupBiometric} disabled={busy}>
              {busy ? t('common.loading') : '🔐 Register Biometric'}
            </button>
            <button className="btn btn-outline" style={{ marginTop: 12, width: '100%' }}
              onClick={completeLogin}>
              Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
